import "components/pages/LessonsStandards/lessonsStandards.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faSearch } from "@fortawesome/free-solid-svg-icons";
import { constants, logEducatorDashboard, LogLevel } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { trackClickedButton } from "utils/analytics";
import { SIDEBAR_BUTTONS, sidebarGradeValue, useSidebarTracking } from "hooks/useSidebarTracking";
import { useStandards } from "hooks/useStandards/useStandards";
import { StandardsFallback } from "components/shared/StandardsFallback/StandardsFallback";
import { useSemanticStandardSearch } from "hooks/useSemanticStandardSearch";
import { useEducatorAccount } from "hooks/useEducatorAccount/useEducatorAccount";
import { useTeacherGradeDefault } from "hooks/useTeacherGradeDefault/useTeacherGradeDefault";
import { withPanelViewMemory } from "hooks/usePageViewTracker";
import { findSelectionByCode, getGradeLabel, gradeHasVisibleStandards, standardsFrameworkId, isAssignableTarget } from "utils/standards";
import { serializeQuestionFilter } from "components/pages/LessonsStandards/questionFilters";
import { StandardNavigationTree } from "components/pages/GroupCodeStandardSearch/StandardNavigationTree";
import { StandardDetailView } from "components/pages/GroupCodeStandardSearch/StandardDetailView";
import { SearchResultsView } from "components/pages/GroupCodeStandardSearch/SearchResultsView";
import logo from "assets/OKO_Core_Logotype2.svg";
import arrow_left_circle from "assets/arrow-left-circle.svg";

// Full-page "Create Quick Group" experience. Mirrors the LessonsStandards
// standard-mode picker (sidebar tree + StandardDetailView in pickerMode, which
// renders the Core/Prerequisites/Extensions tabs + question preview with no
// internal footer), plus a bottom bar reflecting the currently focused standard
// and a Create action that claims a standalone group code via POST /quick-groups.
// Figma node 3805-29896. Reachable from both Groups empty states (differentiation
// and lesson flow) — quick groups are not flow-gated.

// Left-side label in the fixed bottom bar; pushes the CTAs to the right.
// Text style per Figma 4259-5318: "STANDARD - {code}" regular + name bold,
// both green (#002e19), Inter 16px / 22px / 0.25px.
const SelectedStandard = styled.div`
  margin-right: auto;
  color: #002e19;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  max-width: 60%;
  & .code {
    text-transform: uppercase;
  }
  & .name {
    font-weight: 700;
  }
`;

// Why the Create action is unavailable, beside the button. A disabled button can't
// surface a tooltip, and greying it out with no reason reads as "this standard is
// not allowed" when the real cause is usually the class's code supply.
const DisabledReason = styled.span`
  color: #6b6967;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  margin-right: 16px;
  text-align: right;
`;

const disabledPrimaryStyle = { backgroundColor: "#d5d3ce", borderColor: "#d5d3ce", cursor: "not-allowed" };

// "Current Selection" banner shown under the header in edit mode (Figma 3914-19600).
const CurrentSelectionBar = styled.div`
  align-items: center;
  background: #fff;
  color: #002e19;
  display: flex;
  flex-wrap: wrap;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  gap: 8px;
  line-height: 20px;
  padding: 16px 32px;
  & .label {
    font-weight: 700;
  }
`;

const CreateQuickGroupPage = () => {
  const history = useHistory();
  const trackSidebar = useSidebarTracking();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const classroomId = urlParams.get("classroomId");
  const classId = urlParams.get("classId") || classroomId;

  // Edit mode: launched from a quick group card's pencil. editGroupId + editCode
  // switch the page to "Group X: Edit Content" with a Save CTA (updates the
  // group's standard) instead of Create. editStandard/-Name back the banner.
  const editCode = urlParams.get("editCode");
  const editGroupId = urlParams.get("editGroupId");
  const editStandard = urlParams.get("standard");
  const editStandardName = urlParams.get("standardName");
  // The class's last-worked grade, passed by the dashboard. Only the grade is
  // seeded — a quick group targets a standard of the teacher's choosing, so
  // nothing arrives pre-selected.
  const initialGrade = urlParams.get("grade");
  const isEditMode = Boolean(editCode && editGroupId);

  const standardsData = useStandards(true);
  const { gradeLevels, loaded: educatorLoaded, activeSchool } = useEducatorAccount();
  // A state-framework school (its active school names a framework id) must
  // surface zero-mapped standards so the teacher can pick them and read the
  // zero-count warning; a Common Core school keeps the existing hidden rule.
  const showZeroCountStandards = standardsFrameworkId(activeSchool) !== null;
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedGrade, setSelectedGrade] = useState("all");

  // The Advanced Selection the teacher applied while picking. StandardDetailView
  // reports the whole `${trackType}|${code}` map; a quick group is one group on one
  // standard, so only the core entry is meaningful and the API takes a single filter.
  const [pickerFilters, setPickerFilters] = useState({});
  const questionFilter = serializeQuestionFilter(pickerFilters["core|"]) || null;
  // On PATCH the key's PRESENCE decides: absent keeps the stored filter, null clears
  // it. The panel reports an empty map on mount, so sending unconditionally would
  // wipe a teacher's filter whenever they edited something else. Only once a real
  // filter has been reported do we own the value — and can then also clear it.
  const [filterTouched, setFilterTouched] = useState(false);
  const reportFilters = (next) => {
    setPickerFilters(next);
    if (serializeQuestionFilter(next?.["core|"])) setFilterTouched(true);
  };
  const questionFilterPatch = filterTouched ? { question_filter: questionFilter } : {};

  const [availability, setAvailability] = useState(null); // { used, total }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Edit mode opens on the group's current standard — selected, grade filtered
  // and the tree expanded to it — rather than an empty detail panel. Applied once,
  // as soon as the standards land, so it never overrides a later manual pick.
  const restoredEditStandard = useRef(false);
  useEffect(() => {
    // Wait for the account too: the framework id comes from the active school,
    // and a restore applied before it lands would filter the wrong rows.
    if (!isEditMode || restoredEditStandard.current || !educatorLoaded) return;
    const found = findSelectionByCode(standardsData, editStandard, showZeroCountStandards);
    if (!found) return;
    restoredEditStandard.current = true;
    setSelectedStandard({ ...found.entity, grade: found.grade, domain: found.domain, cluster: found.cluster });
    setSelectedGrade(found.grade);
    setExpandedNodes((prev) => ({
      ...prev,
      [found.grade]: true,
      [found.domain.code]: true,
      ...(found.cluster ? { [found.cluster.code]: true } : {}),
    }));
  }, [isEditMode, editStandard, standardsData, showZeroCountStandards, educatorLoaded]);

  const {
    query: searchQuery,
    results: searchResults,
    showResults: showSearchResults,
    handleInputChange: handleSearchInputChange,
    handleSubmit: handleSearchSubmit,
    clearSearch,
    inputRef: searchInputRef,
  } = useSemanticStandardSearch();

  // Load code availability so we can disable Create up front when every code is
  // already in use (still handle 409 on submit — a code can be taken between render
  // and submit by another tab or a bundle assignment).
  useEffect(() => {
    if (!classroomId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await request(`quick-groups/classrooms/${classroomId}`, { method: "GET" });
        if (!cancelled) setAvailability({ used: data?.used ?? 0, total: data?.total ?? 0 });
      } catch (e) {
        if (!cancelled) {
          logEducatorDashboard("CreateQuickGroup: failed to load code availability", {
            level: LogLevel.WARNING,
            event: "quick_group_availability_error",
            statusCode: e?.status,
            classroomId,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classroomId]);

  const availableGrades = useMemo(
    () =>
      (standardsData || [])
        .filter((g) => gradeHasVisibleStandards(g, showZeroCountStandards))
        .map((g) => g.grade),
    [standardsData, showZeroCountStandards]
  );

  // First open defaults the grade dropdown to the class's last-worked grade when
  // the dashboard passed one, else the teacher's grade — both through the same
  // "is it actually an option" check. A manual pick or an edit-mode standard
  // still wins.
  const defaultGradeLevels = useMemo(
    () => (initialGrade ? [initialGrade] : gradeLevels),
    [initialGrade, gradeLevels],
  );
  useTeacherGradeDefault(defaultGradeLevels, educatorLoaded && !isEditMode, availableGrades, setSelectedGrade);

  const filteredStandards = useMemo(
    () =>
      selectedGrade === "all"
        ? standardsData || []
        : (standardsData || []).filter((g) => g.grade === selectedGrade),
    [standardsData, selectedGrade]
  );

  const handleToggleNode = (nodeId) => setExpandedNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));

  const handleSelectStandard = (entity, grade, domain, cluster) => {
    setSelectedStandard({ ...entity, grade, domain, cluster });
    // Selecting opens the node and its path, matching the lesson tree and the
    // create-groups picker — a clicked domain should reveal what is inside it.
    setExpandedNodes((prev) => ({
      ...prev,
      ...(grade ? { [grade]: true } : {}),
      ...(domain?.code ? { [domain.code]: true } : {}),
      ...(cluster?.code ? { [cluster.code]: true } : {}),
      ...(entity?.code ? { [entity.code]: true } : {}),
    }));
    if (showSearchResults) clearSearch();
  };

  const handleGradeChange = (e) => {
    const grade = e.target.value;
    trackSidebar(SIDEBAR_BUTTONS.GRADE_FILTER, { grade: sidebarGradeValue(grade) });
    setSelectedGrade(grade);
    if (grade !== "all") setExpandedNodes((prev) => ({ ...prev, [grade]: true }));
  };

  const handleBack = () =>
    history.push(classId ? `${constants.EDUCATOR_PATH}?classId=${classId}` : constants.EDUCATOR_PATH);

  // A standard, cluster or domain can back a quick group; a whole grade can't.
  // Quick groups have no tracks, so nothing here depends on relationships.
  const canAssignSelection = isAssignableTarget(selectedStandard);
  const allCodesInUse = Boolean(availability && availability.total > 0 && availability.used >= availability.total);
  const noCodes = Boolean(availability && availability.total === 0);
  const canCreate = canAssignSelection && !submitting && !allCodesInUse && !noCodes;
  // In edit mode we update the existing group's standard, so code-availability
  // gating doesn't apply — only a valid target is required.
  const canSave = canAssignSelection && !submitting;
  const canSubmit = isEditMode ? canSave : canCreate;
  // Say which gate is closed. The code-availability ones used to grey the button out
  // with no explanation, which reads as "this standard can't be picked".
  const disabledReason = canSubmit ? ""
    : !canAssignSelection ? "Select a standard, cluster, or domain (not a whole grade)"
      : noCodes ? "This class has no quick group codes"
        : allCodesInUse ? "Every quick group code in this class is already in use"
          : "";
  const standardsLoading = !standardsData || standardsData.length === 0;

  const handleCreate = async () => {
    if (!canCreate) return;
    setSubmitting(true);
    setError(null);
    try {
      // session_length omitted → server defaults to 15 min (no input in the design).
      const result = await request("quick-groups", {
        method: "POST",
        payload: {
          classroom_id: classroomId,
          standard_code: selectedStandard.code,
          // Omitted when the teacher narrowed nothing — the group keeps the full bank.
          ...questionFilterPatch,
        },
      });
      trackClickedButton("Create Quick Group", {
        page_name: "Create Quick Group",
        class: classroomId || null,
        standard_code: result?.standard_code || selectedStandard.code,
        group_code: result?.code || null,
      });
      // Land the educator on the Groups list, where the new quick group now
      // shows in the Quick Groups section (design 3805-26076).
      history.push(classId ? `${constants.EDUCATOR_PATH}?classId=${classId}` : constants.EDUCATOR_PATH);
    } catch (e) {
      logEducatorDashboard("CreateQuickGroup: failed to create quick group", {
        level: LogLevel.WARNING,
        event: "quick_group_create_error",
        statusCode: e?.status,
        classroomId,
      });
      if (e?.status === 400) {
        setError(e?.detail || "That standard isn't available. Please pick another.");
      } else if (e?.status === 409) {
        setError("All group codes in this class are in use. Free one up by deleting a quick group or unassigning a code.");
        setAvailability((prev) => (prev ? { ...prev, used: prev.total } : prev));
      } else if (e?.status === 422) {
        setError("Couldn't create the quick group. Please try again.");
      } else {
        setError(e?.detail || e?.message || "Failed to create quick group. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Edit mode: update the existing quick group's standard rather than claiming a
  // new code. PATCH the classroom-scoped resource (same shape as the release
  // DELETE quick-groups/classrooms/{classroomId}/{groupId}).
  const handleSave = async () => {
    if (!canSave || !isEditMode) return;
    setSubmitting(true);
    setError(null);
    try {
      await request(`quick-groups/classrooms/${classroomId}/${editGroupId}`, {
        method: "PATCH",
        // Only provided fields change. The filter key is sent only once the teacher
        // has touched it, so editing the standard alone leaves it intact.
        payload: { standard_code: selectedStandard.code, ...questionFilterPatch },
      });
      trackClickedButton("Save Quick Group", {
        page_name: "Edit Quick Group",
        class: classroomId || null,
        standard_code: selectedStandard.code,
        group_code: editCode || null,
      });
      history.push(classId ? `${constants.EDUCATOR_PATH}?classId=${classId}` : constants.EDUCATOR_PATH);
    } catch (e) {
      logEducatorDashboard("CreateQuickGroup: failed to save quick group", {
        level: LogLevel.WARNING,
        event: "quick_group_save_error",
        statusCode: e?.status,
        classroomId,
      });
      if (e?.status === 400) {
        setError(e?.detail || "That standard isn't available. Please pick another.");
      } else {
        setError(e?.detail || e?.message || "Failed to save the quick group. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lessons-standards">
      <div className="ls-sidebar ls-sidebar--picker">
        <div className="ls-sidebar-header">
          <img src={logo} alt="OKO" className="ls-sidebar-logo" />
          <button
            className="ls-back-button"
            onClick={() => {
              trackSidebar(SIDEBAR_BUTTONS.BACK_TO_GROUPS);
              handleBack();
            }}
          >
            <img src={arrow_left_circle} alt="back" style={{ width: 20 }} />
            Return to Groups
          </button>
          <select
            className="ls-grade-dropdown"
            aria-label="Grade"
            value={selectedGrade}
            onChange={handleGradeChange}
          >
            <option value="all">All Grades</option>
            {availableGrades.map((grade) => (
              <option key={grade} value={grade}>
                {getGradeLabel(grade)}
              </option>
            ))}
          </select>
        </div>

        <div className="ls-sidebar-content">
          {standardsLoading ? (
            <div className="ls-loading">
              <StandardsFallback />
            </div>
          ) : (
            <StandardNavigationTree
              standards={filteredStandards}
              selectedStandard={selectedStandard}
              expandedNodes={expandedNodes}
              onToggleNode={handleToggleNode}
              onSelectStandard={handleSelectStandard}
              showZeroCountStandards={showZeroCountStandards}
            />
          )}
        </div>
      </div>

      <div className="ls-main-content">
        <div className="ls-content-header">
          <h1 className="ls-header-title">
            {isEditMode ? `Group ${editCode}: Edit Content` : "Create Quick Group"}
          </h1>
          <div className="ls-header-search">
            <span className="ls-header-search-icon">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              ref={searchInputRef}
              className="ls-header-search-input"
              placeholder={'Try: "Multiplying Fractions" or "Proportional relationships"'}
              value={searchQuery}
              onChange={handleSearchInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearchSubmit();
              }}
            />
          </div>
        </div>

        {isEditMode && (
          <CurrentSelectionBar>
            <span className="label">Current Selection:</span>
            {editStandard && <span>{editStandard}:</span>}
            <span>{editStandardName}</span>
          </CurrentSelectionBar>
        )}

        <div className="ls-scrollable-content">
          {standardsLoading ? (
            <div className="ls-loading">
              <StandardsFallback size="2x" />
            </div>
          ) : showSearchResults ? (
            <SearchResultsView
              searchResults={searchResults || []}
              onResultClick={handleSelectStandard}
              onBackClick={clearSearch}
              standardsData={standardsData}
              showZeroCountStandards={showZeroCountStandards}
              activeSchool={activeSchool}
            />
          ) : selectedStandard ? (
            <StandardDetailView
              standard={selectedStandard}
              groupCode={null}
              onStandardClick={handleSelectStandard}
              pickerMode
              onQuestionFiltersChange={reportFilters}
              showZeroCountStandards={showZeroCountStandards}
            />
          ) : (
            <div className="ls-no-selection">
              Select a standard from the sidebar to {isEditMode ? "update this quick group." : "create a quick group."}
            </div>
          )}
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError(null)}
            style={{ position: "fixed", bottom: 88, right: 24, zIndex: 20, maxWidth: 420 }}
          >
            {error}
          </Alert>
        )}

        <div className="ls-assign-bar">
          {/* Only the selected-standard indication renders here; when nothing is
              selected the bar stays white (empty spacer keeps the buttons right). */}
          <SelectedStandard>
            {canAssignSelection && (
              <>
                <span className="code">Standard - {selectedStandard.code}</span>{" "}
                <span className="name">{selectedStandard.name}</span>
              </>
            )}
          </SelectedStandard>
          {/* Browsers don't fire hover on a disabled button, so a title attribute
              there is never seen — say it in the bar instead. */}
          {disabledReason && <DisabledReason role="status">{disabledReason}</DisabledReason>}
          <button className="ls-save-secondary" onClick={handleBack} disabled={submitting}>
            Back to Groups
          </button>
          <button
            className="ls-save-primary"
            onClick={isEditMode ? handleSave : handleCreate}
            disabled={!canSubmit}
            style={!canSubmit ? disabledPrimaryStyle : undefined}
          >
            {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : (isEditMode ? "Save" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
};

export const CreateQuickGroup = withPanelViewMemory(CreateQuickGroupPage);
